import {
  amountToDisplayValue,
  Attachment,
  Button,
  Color,
  FilesPreview,
  getFileThumbnail,
  IconButton,
  ModalConfirmation,
  Text,
  Tooltip,
  useModalMultiple,
  withModalConditionalRender,
} from '@billy/design-system'
import { Eye } from 'lucide-react'
import { MouseEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { AttachmentPreviewModal } from '@features/attachments/elements/AttachmentPreviewModal'

import { useSegment } from '../../../../../../../../hooks'
import { getImageClassName } from '../../../../../../../../utils/getClassName'
import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import * as Styled from './styles'

const DeleteConfirmationModal = withModalConditionalRender(ModalConfirmation)
const PreviewAttachmentModal = withModalConditionalRender(AttachmentPreviewModal)

interface AttachmentCardProps {
  attachment: Attachment
  onRemove?: (attachmentId: string) => void
}

export const AttachmentCard = ({ attachment, onRemove }: AttachmentCardProps) => {
  const { amount, supplier, file, id } = attachment

  const { track } = useSegment()
  const attachmentImageFile = [{ src: getFileThumbnail(file, 'small') }]

  const { t } = useTranslation()

  const formattedAmount = amount ? amountToDisplayValue(amount) : null

  const deleteModalId = `delete-attachment-confirmation-${id}`
  const previewModalId = `preview-attachment-modal-${id}`
  const { open: openModal, close: closeModal } = useModalMultiple([deleteModalId, previewModalId])

  const { originatorType, transactionId } = useTransactionDrawerContext()

  const handleCardClick = useCallback(() => {
    openModal(previewModalId)
    track('xxx - Bookkeeping - Ledger - Drawer', {
      action: 'Open attachment',
      transactionId,
      transactionType: originatorType,
    })
  }, [openModal, originatorType, previewModalId, track, transactionId])

  const handleConfirmDelete = useCallback(() => {
    onRemove?.(id)
    track('xxx - Bookkeeping - Ledger - Drawer', {
      action: 'Delete attachment',
      transactionId,
      transactionType: originatorType,
    })
  }, [id, onRemove, originatorType, track, transactionId])

  const handleRemoveButtonClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      openModal(deleteModalId)
    },
    [deleteModalId, openModal],
  )

  return (
    <>
      <Styled.AttachmentCardWrapper onClick={handleCardClick} readOnly={!onRemove}>
        <Styled.AttachmentImageWrapper>
          <FilesPreview
            className={getImageClassName()}
            files={attachmentImageFile}
            fitToWidth
            withAnnotations={false}
            withNavigation={false}
            fitType="cover"
          />
          <Styled.AttachmentImagePreviewIcon>
            <Eye size={20} color={Color.Black} />
          </Styled.AttachmentImagePreviewIcon>
        </Styled.AttachmentImageWrapper>
        <Styled.AttachmentCardContent>
          <Text variant="bodyLG" truncate weight="semiBold">
            {file.fileName}
          </Text>
          <Text variant="bodySM">{[supplier, formattedAmount].filter(Boolean).join(' · ')}</Text>
        </Styled.AttachmentCardContent>
        {onRemove && (
          <Tooltip label={t('ledger.action.delete_attachment')} placement="bottom">
            <Styled.RemoveButton>
              <IconButton icon="trash" size="sm" onClick={handleRemoveButtonClick} />
            </Styled.RemoveButton>
          </Tooltip>
        )}
      </Styled.AttachmentCardWrapper>
      {onRemove && (
        <DeleteConfirmationModal
          cancelLabel={t('cancel')}
          danger
          id={deleteModalId}
          message={t('bill.action.delete_attachment.message')}
          okLabel={t('bill.action.delete_attachment.ok_button')}
          onOk={handleConfirmDelete}
          title={t('bill.action.delete_attachment.title')}
        />
      )}
      <PreviewAttachmentModal
        id={previewModalId}
        attachment={attachment}
        footerButton={
          <Button fullWidth variant="tonal" onClick={() => closeModal(previewModalId)}>
            {t('close')}
          </Button>
        }
      />
    </>
  )
}
